import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ConnectionsService } from 'src/app/core/service/connections.service';
import { NotificationsService } from 'src/app/core/service/notifications.service';
import * as NotificationsActions from 'src/app/core/state/notifications/notifications.action';
import { NotificationCounters } from 'src/app/core/state/notifications/notifications.model';
import { getNotificationCounters } from 'src/app/core/state/notifications/notifications.selectors';
import { getBrandDetails } from 'src/app/core/state/global';
import { IBrandDetails } from 'src/app/core/domain/brand.model';
import { APPLICATION_STATUS_ICONS, COMMUNITY_ACTIVITY_ICONS, EVENT_REMINDER_ICON, NotificationTypes } from 'src/app/modules/notifications/notifications.enum';
import { StorageService } from 'src/app/shared/utils/storage.service';
import { ToastAlertService } from 'src/app/shared/utils/toast.service';
import { ConnectionRequestTypes } from 'src/cometchat-pro-angular-ui-kit/CometChatWorkspace/src/utils/enums';
import { htmlToText } from '../../utils/html-to-text.util';

export type BellTab = 'all' | 'action';

const PANEL_PAGE_SIZE = 20;
const BELL_CLEARED_KEY = 'notificationBellCleared';

/**
 * SAN-1432 / SAN-1433 / SAN-1434 (T4.5–T4.7, BRD FR-U5, W-02) — header bell.
 * Bell number = counters.bell (red + orange counters; mode-A live counts
 * excluded, so it can reach 0). The panel lists GET notifications newest
 * first, in Today / Earlier groups, with an All / Needs action filter.
 * Render only when notification_centre_enabled is on (the host checks it).
 */
@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  // 'header' = white icon on the coloured top bar; 'sidebar' = dark icon.
  @Input() variant: 'header' | 'sidebar' = 'header';

  NotificationTypes = NotificationTypes;
  // SAN-1461 — keyboard focus targets.
  @ViewChild('panelHeading') panelHeading: ElementRef<HTMLElement>;
  @ViewChild('tabAll') tabAll: ElementRef<HTMLButtonElement>;
  @ViewChild('tabAction') tabAction: ElementRef<HTMLButtonElement>;
  counters: NotificationCounters | null = null;
  brandDetails: Partial<IBrandDetails> | null = null;
  items: any[] = [];
  tab: BellTab = 'all';
  loading = false;
  loaded = false;
  markingAll = false;
  // uuid → in-flight accept/decline, so a double click can't send twice.
  acting: { [uuid: string]: boolean } = {};
  // Connections accepted / declined from this panel, so a refetch can't
  // bring their buttons back before the backend reports them as actioned.
  private actedConnections = new Set<string>();
  // The "new" part of the bell at the last "Mark all as read". The bell
  // shows only new items that came in after it; pending connection requests
  // and unopened job applications always count until acted on (FR-U5).
  private bellBaseline = 0;

  private destroyed$ = new Subject<void>();

  constructor(
    private store: Store<any>,
    private router: Router,
    private notificationsService: NotificationsService,
    private connectionsService: ConnectionsService,
    private toastService: ToastAlertService,
    private storageService: StorageService,
  ) { }

  ngOnInit(): void {
    this.bellBaseline = Number(this.storageService.getItem(this.bellClearedKey())) || 0;
    this.store.pipe(select(getNotificationCounters), takeUntil(this.destroyed$)).subscribe((res) => {
      this.counters = res;
      // Items handled since the clear lower the total; lower the baseline
      // with it so newer arrivals still show up on the bell.
      if (res && this.newPart(res) < this.bellBaseline) this.saveBellBaseline(this.newPart(res));
    });
    this.store.pipe(select(getBrandDetails), takeUntil(this.destroyed$)).subscribe((res) => {
      this.brandDetails = res;
    });
  }


  get bellCount(): number {
    if (!this.counters) return 0;
    return this.actionPart(this.counters) + Math.max(0, this.newPart(this.counters) - this.bellBaseline);
  }

  // Bell terms that wait on the user (same muting as the sidebar badges).
  private actionPart(c: NotificationCounters): number {
    const muted = c.inAppMuted || [];
    return (muted.includes('connectionRequests') ? 0 : Number(c.connections) || 0)
      + (muted.includes('jobApplications') ? 0 : Number(c.jobs?.total) || 0);
  }

  private newPart(c: NotificationCounters): number {
    return Math.max(0, (c.bell || 0) - this.actionPart(c));
  }

  // Needs action = still waiting on the user; once acted on it drops out.
  get visibleItems(): any[] {
    return this.tab === 'action' ? this.items.filter((i) => i.isActionRequired && !i.isActioned) : this.items;
  }

  // The All tab shows the bell's own number, so the two always agree. The
  // bell also counts items already read but not yet acted on (a job
  // application not opened, a pending connection request).
  get unreadCount(): number {
    return this.bellCount;
  }

  get actionCount(): number {
    return this.items.filter((i) => i.isActionRequired && !i.isActioned).length;
  }

  get todayItems(): any[] {
    return this.visibleItems.filter((i) => i.group === 'today');
  }

  get earlierItems(): any[] {
    return this.visibleItems.filter((i) => i.group !== 'today');
  }

  onOpenChange(open: boolean): void {
    if (!open) return;
    this.fetchItems();
    // SAN-1461 (WCAG 2.1.1 / 2.4.3) — the header panel is appended to <body>,
    // so Tab from the bell would never reach it: move focus into the panel.
    setTimeout(() => this.panelHeading?.nativeElement.focus());
  }

  // SAN-1461 — Left / Right arrows switch the All / Needs action tab.
  switchTab(event: Event): void {
    event.preventDefault();
    this.tab = this.tab === 'all' ? 'action' : 'all';
    setTimeout(() => (this.tab === 'all' ? this.tabAll : this.tabAction)?.nativeElement.focus());
  }

  // SAN-1461 (WCAG 1.3.1) — the unread / needs-action dot, for screen readers.
  readStateLabel(item: any): string {
    if (item?.isActionRequired && !item?.isActioned) return 'Needs action.';
    return item?.isRead ? '' : 'Unread.';
  }

  fetchItems(): void {
    this.loading = true;
    this.notificationsService.fetchNotifications({ pageNumber: 1, limit: PANEL_PAGE_SIZE })
      .pipe(finalize(() => { this.loading = false; this.loaded = true; }))
      .subscribe({
        next: (res) => {
          this.items = (res?.items || []).map((i) => this.normalize(i));
          this.settleHandledRequests();
        },
        error: () => { this.items = []; },
      });
  }

  // A request accepted or declined anywhere else (Connections page, another
  // device) is no longer in the received-pending list: drop its buttons and
  // take it out of Needs action, whatever the backend reports.
  private settleHandledRequests(): void {
    const open = this.items.filter((i) => this.isPendingRequest(i));
    if (!open.length) return;
    this.connectionsService.getRequestedConnections(ConnectionRequestTypes.RECEIVED).subscribe({
      next: (res) => {
        const pending = new Set((res?.data?.items || []).map((c) => c.connectionUUID));
        open.filter((i) => !pending.has(i.object.uuid)).forEach((i) => {
          i.isActioned = true;
          this.actedConnections.add(i.object.uuid);
        });
      },
      error: () => { },
    });
  }

  isPendingRequest(item: any): boolean {
    return item.type === NotificationTypes.ConnectionRequest && !item.isActioned && !!item.object?.uuid;
  }

  // SAN-1471 — icon for an application status item; SAN-1472 — and for an
  // event reminder; SAN-1470 — and for community activity (none for other types).
  statusIcon(item: any): string | null {
    if (item?.type === NotificationTypes.EventReminder) return EVENT_REMINDER_ICON;
    return APPLICATION_STATUS_ICONS[item?.type] || COMMUNITY_ACTIVITY_ICONS[item?.type] || null;
  }

  // SAN-1471 (A-13) — an action-required status item still waiting for the
  // user carries `cta`; the backend sends null once it is done.
  hasCta(item: any): boolean {
    return !!(item?.cta?.label && item?.cta?.url && item.isActionRequired && !item.isActioned);
  }

  // Opening it marks it read but keeps it under Needs action until the
  // backend reports the action done (A-12).
  openCta(item: any, event: Event): void {
    event.stopPropagation();
    this.markItemRead(item);
    this.navigateToUrl(item.cta.url);
  }

  open(item: any): void {
    this.markItemRead(item);
    if (item.type === NotificationTypes.ConnectionRequest || item.type === NotificationTypes.ConnectionAction) {
      this.router.navigate(['/connections/pending-requests']);
      return;
    }
    // Live rows carry uuid "live:<type>:<id>"; the program's own uuid is object.uuid.
    if (item.type === NotificationTypes.LiveProgram && item.object?.uuid) {
      this.router.navigate(['/call-for-applications'], { queryParams: { programId: item.object.uuid } });
      return;
    }
    this.navigateToUrl(item.url);
  }

  respond(item: any, accept: boolean, event: Event): void {
    event.stopPropagation();
    const connectionUUID = item.object?.uuid;
    if (!connectionUUID || this.acting[item.uuid]) return;
    // Accept / Decline act in place; the row itself still opens the
    // Connections page for the full Accept dialog.
    this.acting[item.uuid] = true;

    const payload = { connectionUUID, actionMessage: '' };
    const request$ = accept
      ? this.connectionsService.acceptConnection(payload, { silentError: true })
      : this.connectionsService.rejectConnection(payload, { silentError: true });

    request$.pipe(finalize(() => { this.acting[item.uuid] = false; })).subscribe({
      next: () => {
        // acceptConnection already toasts the API's own success message.
        if (!accept) this.toastService.showToast('Connection request declined', 'success');
        // Badge, bell and Received count drop at once; the refetch reconciles.
        this.store.dispatch(new NotificationsActions.ConnectionRequestHandled());
        this.settle(item);
      },
      error: (fault) => {
        // A 4xx here means the request was withdrawn or handled elsewhere.
        const handledElsewhere = fault?.status >= 400 && fault?.status < 500;
        this.toastService.showToast(
          handledElsewhere
            ? 'This request was already withdrawn or handled, so it has been removed.'
            : fault?.error?.message || 'Could not update the connection request. Please try again.',
          handledElsewhere ? 'info' : 'error',
        );
        if (handledElsewhere) this.settle(item);
      },
    });
  }

  markAllRead(): void {
    if (this.markingAll) return;
    this.markingAll = true;
    this.notificationsService.markAllReadNotifications()
      .pipe(finalize(() => { this.markingAll = false; }))
      .subscribe({
        next: () => {
          // Backend clears "new" items only; action-required ones stay (FR-U5).
          this.items.forEach((i) => { if (!i.isActionRequired) i.isRead = true; });
          this.saveBellBaseline(this.counters ? this.newPart(this.counters) : 0);
          this.refreshCounters();
        },
        error: () => { },
      });
  }

  viewAll(): void {
    this.router.navigate(['/notifications']);
  }

  trackByUuid(index: number, item: any): string {
    return item.uuid || `${index}`;
  }

  // The bell fields come from the backend; fall back to the same rules it
  // uses (a connection request needs action; Today = same calendar day) so
  // a missing field never hides a request from Needs action.
  private normalize(item: any): any {
    const createdAt = item.createdAt ? new Date(item.createdAt) : null;
    return {
      ...item,
      message: htmlToText(item.message),
      isRead: !!item.isRead,
      isActionRequired: item.isActionRequired ?? item.type === NotificationTypes.ConnectionRequest,
      isActioned: !!item.isActioned || this.actedConnections.has(item.object?.uuid),
      group: item.group || (createdAt && createdAt.toDateString() === new Date().toDateString() ? 'today' : 'earlier'),
    };
  }

  private markItemRead(item: any): void {
    // SAN-1769 — live items have no notifications row to mark; they leave
    // the list once the item is opened or its section visited.
    if (item.uuid && !item.isRead && !item.isLive) {
      this.notificationsService.markRead(item.uuid).subscribe({
        next: () => this.refreshCounters(),
        error: () => { },
      });
      item.isRead = true;
    }
  }

  private settle(item: any): void {
    item.isRead = true;
    item.isActioned = true;
    if (item.object?.uuid) this.actedConnections.add(item.object.uuid);
    if (item.uuid) this.notificationsService.markRead(item.uuid).subscribe({ error: () => { } });
    this.refreshCounters();
  }

  private bellClearedKey(): string {
    return `${BELL_CLEARED_KEY}:${this.storageService.getObject(StorageService.storageKeys.user)?.userId || ''}`;
  }

  private saveBellBaseline(value: number): void {
    this.bellBaseline = value;
    this.storageService.setItem(this.bellClearedKey(), String(value));
  }

  private refreshCounters(): void {
    this.store.dispatch(new NotificationsActions.SetNotificationsCount());
  }

  private navigateToUrl(url: string): void {
    if (!url) return;
    try {
      const target = new URL(url, location.origin);
      if (target.origin === location.origin) {
        this.router.navigateByUrl(target.pathname + target.search + target.hash);
      } else {
        window.open(target.href, '_blank', 'noopener');
      }
    } catch (e) { }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
